import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sun, Clock, Calendar, Users, Heart, Shield, Star, Sparkles, Trophy, Smile, MapPin, Phone, Download, CreditCard, Utensils, Bus, Award } from "lucide-react";
import SEOHead from "@/components/SEOHead";

const IMAGES = {
  hero: "/summer-group-all.jpg",
  coachKids: "/summer-coach-kids.jpg",
  outdoorFun: "/summer-outdoor-fun.jpg",
  basketballKids: "/summer-basketball-kids.jpg",
  selfieFun: "/summer-selfie-fun.jpg",
};

export default function SummerSchoolPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Yaz Spor Okulu - İRÜ Spor Kulübü",
    "description": "5-14 yaş grubu için voleybol, basketbol ve cimnastik yaz spor okulu programı. Ataşehir, Kadıköy, Maltepe bölgesi.",
    "provider": {
      "@type": "SportsOrganization",
      "name": "İRÜ Spor Kulübü",
      "address": { "@type": "PostalAddress", "addressLocality": "İstanbul", "addressRegion": "Kadıköy" }
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "onsite",
      "location": {
        "@type": "Place",
        "name": "İstanbul Rumeli Üniversitesi Bostancı Yerleşkesi",
        "address": { "@type": "PostalAddress", "addressLocality": "İstanbul", "addressRegion": "Kadıköy" }
      }
    }
  };

  return (
    <div data-testid="summer-school-page" className="pt-20">
      <SEOHead
        title={"Yaz Spor Okulu - Çocuklar İçin Yaz Kampı Ataşehir, Kadıköy, Maltepe"}
        description={"5-14 yaş grubu için yaz spor okulu. Voleybol, basketbol, cimnastik eğitimi. Ataşehir, Kadıköy, Maltepe bölgesinde çocuk yaz kampı. Profesyonel antrenörler, eğlenceli program."}
        keywords={"yaz spor okulu İstanbul, çocuk yaz kampı Kadıköy, yaz spor kampı Ataşehir, çocuk yaz okulu Maltepe, Anadolu yakası yaz spor okulu, yaz etkinlikleri çocuk"}
        canonical="/yaz-spor-okulu"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt={"Yaz Spor Okulu Ataşehir Kadıköy Maltepe"} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/90 via-[#1A1A1A]/70 to-[#1A1A1A]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-gold/20 border border-gold/30 px-4 py-1.5 mb-6">
              <Sun className="w-4 h-4 text-gold" />
              <span className="text-xs font-accent uppercase tracking-widest text-white/90">{"2026 Yaz Programı"}</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white leading-[1.1]">
              {"Spor ve Sanat"}<br /><span className="text-gold">Yaz Okulu</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/70 leading-relaxed max-w-lg">
              {"Eğlen, Öğren, Geliş! 5-14 yaş grubu için 14 farklı branşta profesyonel eğitim. Bostancı Kampüsü'nde uzman antrenörler eşliğinde unutulmaz bir yaz."}
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2">
                <Calendar className="w-4 h-4 text-gold" />
                <span className="text-sm text-white/90 font-accent">{"29 Haz - 28 Ağu 2026"}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2">
                <Clock className="w-4 h-4 text-gold" />
                <span className="text-sm text-white/90 font-accent">09:00 - 17:00</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2">
                <Users className="w-4 h-4 text-gold" />
                <span className="text-sm text-white/90 font-accent">{"5-14 Yaş"}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/on-kayit">
                <Button data-testid="summer-hero-register" className="bg-gold hover:bg-yellow-600 text-[#1A1A1A] font-heading uppercase tracking-wider text-sm px-8 py-6">
                  {"Hemen Kayıt Ol"} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="https://wa.me/905436470183" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-heading uppercase tracking-wider text-sm px-8 py-6">
                  <Phone className="w-4 h-4 mr-2" /> Bilgi Al
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 4 Temel Ozellik */}
      <section className="py-12 bg-white border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Trophy, title: "14 Branş", desc: "Spor ve sanat bir arada" },
              { icon: Shield, title: "Güvenli Kampüs", desc: "Üniversite yerleşkesinde" },
              { icon: Users, title: "Uzman Kadro", desc: "Lisanslı antrenörler" },
              { icon: Award, title: "Yetenek Testi", desc: "Spor yatkınlık analizi" },
            ].map((item) => (
              <div key={item.title} className="flex items-center gap-3">
                <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-burgundy/5 border border-burgundy/10">
                  <item.icon className="w-5 h-5 text-burgundy" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#1A1A1A]">{item.title}</p>
                  <p className="text-xs text-gray-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Branslar + Fotograflar */}
      <section className="py-20 lg:py-28 bg-white" data-testid="summer-branches">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-accent text-sm uppercase tracking-widest text-burgundy">{"2026 Yaz Dönemi"}</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2">
                {"Spor ve Sanat Branşlarımız"}
              </h2>
              <p className="mt-4 text-gray-600 leading-relaxed">
                {"14 farklı branşta çocuğunuzun yeteneklerini keşfetmesini sağlıyoruz. Tüm branşlar uzman antrenörler rehberliğinde yürütülmektedir."}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-8">
                {[
                  { name: "Futbol", icon: Trophy },
                  { name: "Basketbol", icon: Trophy },
                  { name: "Voleybol", icon: Trophy },
                  { name: "Yüzme", icon: Trophy },
                  { name: "Cimnastik", icon: Trophy },
                  { name: "Tenis", icon: Trophy },
                  { name: "Masa Tenisi", icon: Trophy },
                  { name: "Kids Game", icon: Smile },
                  { name: "Görsel Sanatlar", icon: Sparkles },
                  { name: "Drama", icon: Star },
                  { name: "Plaj Voleybolu", icon: Sun },
                  { name: "Dans", icon: Heart },
                  { name: "Satranç", icon: Award },
                  { name: "Survivor", icon: Shield },
                ].map((b) => (
                  <div key={b.name} className="flex items-center gap-2 bg-surface border border-zinc-100 px-3 py-2.5 hover:border-burgundy/30 transition-colors">
                    <b.icon className="w-3.5 h-3.5 text-burgundy shrink-0" strokeWidth={1.5} />
                    <span className="text-sm font-medium text-[#1A1A1A]">{b.name}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-8">
                {[
                  { icon: Users, text: "Her grupta 1 Lider + 1 Asistan" },
                  { icon: Bus, text: "Servis imkânı mevcut" },
                  { icon: Utensils, text: "Öğle yemeği + ara öğün" },
                  { icon: Clock, text: "09:00 - 17:00 tam gün" },
                ].map((f) => (
                  <div key={f.text} className="flex items-center gap-2 text-xs text-gray-500">
                    <f.icon className="w-3.5 h-3.5 text-burgundy shrink-0" strokeWidth={1.5} />
                    <span>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="aspect-[3/4] overflow-hidden">
                <img src={IMAGES.coachKids} alt={"Antrenör ve çocuklar yaz spor okulu"} className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[3/4] overflow-hidden mt-6">
                <img src={IMAGES.basketballKids} alt={"Basketbol eğitimi yaz okulu"} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gunluk Program */}
      <section className="py-20 lg:py-28 bg-surface" data-testid="daily-schedule">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="font-accent text-sm uppercase tracking-widest text-burgundy">{"Günlük Akış"}</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2 mb-8">
                {"Örnek Günlük Program"}
              </h2>
              <div className="space-y-0 border border-zinc-200 overflow-hidden bg-white">
                {[
                  { time: "09:00", title: "Karşılama ve Toplanma", desc: "Sabah sporu ile güne başlangıç", color: "bg-gold" },
                  { time: "09:30", title: "1. Branş Eğitimi", desc: "Futbol, Basketbol, Voleybol vb.", color: "bg-burgundy" },
                  { time: "10:30", title: "Mola ve Dinlenme", desc: "", color: "bg-zinc-400" },
                  { time: "10:45", title: "2. Branş Eğitimi", desc: "Yüzme, Tenis, Cimnastik vb.", color: "bg-burgundy" },
                  { time: "12:00", title: "Öğle Yemeği", desc: "Yemek hizmeti dahildir", color: "bg-gold" },
                  { time: "13:00", title: "Atölye Çalışmaları", desc: "Sanat, Drama, Dans", color: "bg-[#1A1A1A]" },
                  { time: "14:00", title: "3. Branş Eğitimi", desc: "Eğitici oyunlar, takım çalışması", color: "bg-burgundy" },
                  { time: "15:15", title: "Ara Öğün", desc: "İkindi atıştırmalığı", color: "bg-gold" },
                  { time: "15:45", title: "Serbest Etkinlikler", desc: "Oyunlar ve sosyal aktiviteler", color: "bg-zinc-500" },
                  { time: "16:45", title: "Hazırlık ve Ayrılış", desc: "17:00'de servis kalkışı", color: "bg-[#1A1A1A]" },
                ].map((item) => (
                  <div key={item.time} className="flex items-stretch border-b border-zinc-100 last:border-b-0">
                    <div className={`${item.color} w-16 sm:w-20 shrink-0 flex items-center justify-center`}>
                      <span className="text-white text-xs sm:text-sm font-bold">{item.time}</span>
                    </div>
                    <div className="flex-1 px-4 py-3">
                      <p className="text-sm font-semibold text-[#1A1A1A]">{item.title}</p>
                      {item.desc && <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-gray-400 italic">{"* Örnek programdır. Saatler değişiklik gösterebilir."}</p>
            </div>
            <div className="space-y-3">
              <div className="aspect-video overflow-hidden">
                <img src={IMAGES.outdoorFun} alt={"Açık hava aktiviteleri yaz okulu"} className="w-full h-full object-cover" />
              </div>
              <div className="aspect-video overflow-hidden">
                <img src={IMAGES.selfieFun} alt={"Eğlenceli anlar yaz kampı"} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Takvim + Ucretler */}
      <section className="py-20 lg:py-28 bg-white" data-testid="summer-brochure">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-accent text-sm uppercase tracking-widest text-burgundy">{"Kayıt Bilgileri"}</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2">
              {"Takvim ve Ücretlendirme"}
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Takvim */}
            <div>
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-[#1A1A1A] mb-4">
                {"2026 Akademik Takvimi"}
              </h3>
              <div className="border border-zinc-200 overflow-hidden">
                <div className="bg-burgundy px-4 py-3 flex items-center justify-between">
                  <span className="text-white text-xs font-heading uppercase tracking-wider">{"Dönem"}</span>
                  <span className="text-white text-xs font-heading uppercase tracking-wider">Tarih</span>
                </div>
                {[
                  { week: "1. Dönem", date: "29 Haziran - 3 Temmuz" },
                  { week: "2. Dönem", date: "6 - 10 Temmuz" },
                  { week: "3. Dönem", date: "13 - 17 Temmuz" },
                  { week: "4. Dönem", date: "20 - 24 Temmuz" },
                  { week: "5. Dönem", date: "27 - 31 Temmuz" },
                  { week: "6. Dönem", date: "3 - 7 Ağustos" },
                  { week: "7. Dönem", date: "10 - 14 Ağustos" },
                  { week: "8. Dönem", date: "17 - 21 Ağustos" },
                  { week: "9. Dönem", date: "24 - 28 Ağustos" },
                ].map((w, i) => (
                  <div key={w.week} className={`px-4 py-2.5 flex items-center justify-between ${i % 2 === 0 ? "bg-white" : "bg-surface"} border-b border-zinc-100 last:border-b-0`}>
                    <span className="text-sm font-medium text-[#1A1A1A]">{w.week}</span>
                    <span className="text-sm text-gray-600">{w.date}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                <Calendar className="w-3.5 h-3.5" />
                <span>{"Başlangıç: 29 Haziran 2026 Pazartesi"}</span>
              </div>
            </div>

            {/* Ucret */}
            <div>
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-[#1A1A1A] mb-4">
                {"Ücretlendirme"}
              </h3>
              <div className="border border-zinc-200 overflow-hidden">
                <div className="bg-[#1A1A1A] px-4 py-3 grid grid-cols-3 gap-2">
                  <span className="text-white text-xs font-heading uppercase tracking-wider">{"Süre"}</span>
                  <span className="text-white text-xs font-heading uppercase tracking-wider text-center">{"İndirim"}</span>
                  <span className="text-white text-xs font-heading uppercase tracking-wider text-right">{"Ücret"}</span>
                </div>
                {[
                  { dur: "1 Hafta", disc: "-", price: "11.000 ₺", hl: false },
                  { dur: "4 Hafta", disc: "%10", price: "39.600 ₺", hl: false },
                  { dur: "5 Hafta", disc: "%10", price: "49.500 ₺", hl: false },
                  { dur: "6 Hafta", disc: "%10", price: "59.400 ₺", hl: false },
                  { dur: "7 Hafta", disc: "%15", price: "65.450 ₺", hl: true },
                  { dur: "8 Hafta", disc: "%15", price: "74.800 ₺", hl: true },
                  { dur: "9 Hafta", disc: "%20", price: "79.200 ₺", hl: true },
                ].map((p, i) => (
                  <div key={p.dur} className={`px-4 py-2.5 grid grid-cols-3 gap-2 border-b border-zinc-100 last:border-b-0 ${p.hl ? "bg-burgundy/5" : i % 2 === 0 ? "bg-white" : "bg-surface"}`}>
                    <span className="text-sm font-medium text-[#1A1A1A]">{p.dur}</span>
                    <span className={`text-sm text-center ${p.disc !== "-" ? "text-burgundy font-semibold" : "text-gray-400"}`}>{p.disc}</span>
                    <span className="text-sm font-bold text-[#1A1A1A] text-right">{p.price}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 space-y-1.5">
                <div className="flex items-start gap-2 text-xs text-gray-500">
                  <Utensils className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>{"Yemek Ücreti: 400 ₺ / Gün (öğle yemeği + ara öğün)"}</span>
                </div>
                <div className="flex items-start gap-2 text-xs text-gray-500">
                  <CreditCard className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>{"İndirimli fiyatlar Nisan sonuna kadar peşin ödemelerde geçerlidir."}</span>
                </div>
                <div className="flex items-start gap-2 text-xs text-gray-500">
                  <Users className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>{"Kardeş indirimi ve toplu kayıt avantajları için bilgi alın."}</span>
                </div>
              </div>
              <Link to="/on-kayit" className="block mt-6">
                <Button data-testid="brochure-register-btn" className="w-full bg-burgundy hover:bg-burgundy-700 text-white font-heading uppercase tracking-wider text-sm py-5">
                  {"Ön Kayıt Oluştur"} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Brosur + Iletisim */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            {/* PDF */}
            <div className="flex items-center gap-6">
              <div className="w-20 h-28 shrink-0 bg-white/10 border border-white/20 overflow-hidden">
                <iframe
                  src="https://customer-assets.emergentagent.com/job_638dd389-f89e-4528-8c19-d6d4e2c4f4b0/artifacts/c8zrdmjv_rumeli_summer_school_20260315142555.pdf"
                  className="w-full h-full pointer-events-none"
                  title={"Broşür"}
                />
              </div>
              <div>
                <p className="text-sm font-bold text-white">{"Tanıtım Broşürü"}</p>
                <p className="text-xs text-white/50 mt-1">{"Detaylı bilgi için broşürümüzü indirin"}</p>
                <a
                  href="https://customer-assets.emergentagent.com/job_638dd389-f89e-4528-8c19-d6d4e2c4f4b0/artifacts/c8zrdmjv_rumeli_summer_school_20260315142555.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="download-brochure-btn"
                  className="inline-flex items-center gap-1.5 text-xs text-gold hover:text-yellow-400 font-accent uppercase tracking-wider mt-2"
                >
                  <Download className="w-3.5 h-3.5" /> {"PDF İndir"}
                </a>
              </div>
            </div>

            {/* Iletisim */}
            <div className="flex items-center gap-6">
              <div>
                <p className="text-sm font-bold text-white">{"Kampus Ziyareti"}</p>
                <p className="text-xs text-white/50 mt-1">{"Tesisleri görmek ve antrenörlerle tanışmak için"}</p>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  <a href="tel:05436470183" className="flex items-center gap-1.5 text-xs text-white/80 hover:text-white">
                    <Phone className="w-3.5 h-3.5 text-gold" /> 0543 647 01 83
                  </a>
                  <span className="flex items-center gap-1.5 text-xs text-white/50">
                    <MapPin className="w-3.5 h-3.5 text-gold" /> {"Bostancı"}
                  </span>
                </div>
              </div>
            </div>

            {/* WhatsApp */}
            <div className="flex justify-start lg:justify-end">
              <a href="https://wa.me/905436470183" target="_blank" rel="noopener noreferrer" data-testid="whatsapp-btn" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BD5B] text-white font-heading uppercase tracking-wider text-sm px-8 py-4 transition-colors">
                <Phone className="w-4 h-4" /> WhatsApp Randevu
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-20 lg:py-28 overflow-hidden" data-testid="summer-cta">
        <div className="absolute inset-0">
          <img src={IMAGES.selfieFun} alt="Yaz Spor Okulu" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1A1A1A]/80" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-white">{"Bu Yaz Unutulmaz Olsun!"}</h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto">
            {"Çocuğunuza spor, eğlence ve yeni arkadaşlıklarla dolu bir yaz hediye edin."}
          </p>
          <Link to="/on-kayit">
            <Button data-testid="summer-final-cta" className="mt-8 bg-gold hover:bg-yellow-600 text-[#1A1A1A] font-heading uppercase tracking-wider text-sm px-10 py-6">
              {"Ön Kayıt Formu"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
